import { Stack, Typography } from '@mui/material';
import { useRef } from 'react';
import { useIntl } from 'react-intl';

import { DropdownSelect, DropdownSelectProps } from './DropdownSelect';
import { DropdownSelectRow } from './DropdownSelectRow';

export type SimpleDropdownSelectOption<T> = { nameTextId?: string; name?: string; value: T };

export type SimpleDropdownSelectProps<T> = {
  value?: T;
  options: SimpleDropdownSelectOption<T>[];
  onChange: (value: T) => void;
} & Omit<DropdownSelectProps, 'children' | 'renderContent' | 'hasValue'>;

export const SimpleDropdownSelect = <T extends string | boolean | null>({
  value,
  options,
  onChange,
  ...rest
}: SimpleDropdownSelectProps<T>) => {
  const ref = useRef<DropdownSelect>(null);
  const selectedOption = options.find((option) => option.value === value);
  const { formatMessage } = useIntl();

  const renderOptionLabel = (option: { nameTextId?: string; name?: string; value: T }) => {
    if (option.nameTextId) {
      return formatMessage({ id: option.nameTextId });
    }

    return option.name ?? String(option.value);
  };

  return (
    <DropdownSelect
      ref={ref}
      hasValues={value !== undefined}
      renderContent={() => (
        <Stack gap={0.25} p={1}>
          {options.map((option) => (
            <DropdownSelectRow
              key={String(option.value)}
              onClick={() => {
                onChange(option.value);
                ref.current?.close();
              }}
              isSelected={option.value === value}
            >
              {renderOptionLabel(option)}
            </DropdownSelectRow>
          ))}
        </Stack>
      )}
      {...rest}
    >
      {() => {
        if (!selectedOption) return null;

        return (
          <Typography variant="h3" noWrap>
            {renderOptionLabel(selectedOption)}
          </Typography>
        );
      }}
    </DropdownSelect>
  );
};
